import Image from 'next/image';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import useClickOutside from '@/hooks/useClickOutside';
import { Country } from '@/services/countries';

type CountryInfo = {
  title: string;
  value: string;
  phoneCode: string;
};

export interface CountrySelectorProps {
  id: string;
  disabled?: boolean;
  onChange: (value: {
    name: string;
    code: string;
    phoneCode: number;
    id: number;
  }) => void;
  selectedValue?: Country;
  label: string;
  buttonClassName?: string;
  showPhoneCode?: boolean;
  countries: Country[];
}

export default function CountrySelect({
  id,
  disabled = false,
  onChange,
  selectedValue,
  label,
  buttonClassName = '',
  showPhoneCode = false,
  countries,
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const ref = useClickOutside({
    clickFn: () => {
      setOpen(false);
      setQuery('');
    },
  });

  return (
    <div ref={ref}>
      <div className="relative">
        <label
          className="text-sm text-gray-700 leading-[1.8] w-fit"
          htmlFor={id}>
          {label}
        </label>
        <button
          id={id}
          type="button"
          className={cn(
            'relative w-full min-h-[44px] h-[2.5625rem] leading-[1.625] border bg-gray-100 border-gray-200 shadow-sm pl-3 pr-[30px] py-2 text-left cursor-pointer focus:outline-none text-base sm:text-sm',
            {
              'border-primary': open,
            },
            buttonClassName,
          )}
          aria-haspopup="listbox"
          aria-expanded="true"
          aria-labelledby="listbox-label"
          onClick={() => setOpen(!open)}
          disabled={disabled}>
          <span className="relative w-full truncate flex items-center text-base">
            <Image
              alt={`${selectedValue?.code}`}
              src={`https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/admin/flags/${selectedValue?.code}.png`}
              className="inline mr-2 rounded-sm"
              width={21}
              height={15}
            />
            {open ? (
              <input
                placeholder={
                  showPhoneCode
                    ? `(+${selectedValue?.phoneCode})`
                    : selectedValue?.name
                }
                autoFocus
                className={cn(
                  'focus:outline-none bg-transparent w-full text-sm',
                  {
                    'z-10 absolute w-[55%] left-[30px]': showPhoneCode,
                  },
                )}
                onChange={(e) => setQuery(e.target.value)}
              />
            ) : showPhoneCode ? (
              `(+${selectedValue?.phoneCode})`
            ) : (
              selectedValue?.name
            )}
          </span>
          <span
            className={`absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none ${
              disabled ? 'hidden' : ''
            }`}>
            <svg
              className="h-5 w-5 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </button>

        <div
          className={cn(
            'max-h-0 absolute z-10 mt-1 w-full md:w-80 bg-white shadow-lg rounded-sm overflow-hidden text-base focus:outline-none sm:text-sm transition-[max-height] duration-200 ease-in-out',
            {
              'max-h-80 border border-gray-200': open,
              'w-80': showPhoneCode,
            },
          )}>
          {open && (
            <div
              className={
                'max-h-64 scrollbar-track-transparent scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-600 scrollbar-thumb-rounded scrollbar-thin overflow-y-scroll'
              }>
              {countries.filter((country) =>
                country.name.toLowerCase().startsWith(query.toLowerCase()),
              ).length === 0 ? (
                <li className="text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9">
                  No countries found
                </li>
              ) : (
                countries
                  .filter((country) =>
                    country.name.toLowerCase().startsWith(query.toLowerCase()),
                  )
                  .map((country, index) => {
                    return (
                      <li
                        key={`${id}-${index}`}
                        className={cn(
                          'text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9 flex items-center hover:bg-gray-50 transition',
                          {
                            'bg-gray-200': country.code === selectedValue?.code,
                          },
                        )}
                        id="listbox-option-0"
                        role="option"
                        aria-selected={country.code === selectedValue?.code}
                        onClick={() => {
                          onChange(country);
                          setQuery('');
                          setOpen(false);
                        }}>
                        <Image
                          alt={`${country.code}`}
                          src={`https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/admin/flags/${country.code}.png`}
                          className="inline mr-2 rounded-sm"
                          width={21}
                          height={15}
                        />

                        {showPhoneCode && (
                          <span className="font-normal truncate text-sm mr-2 text-gray-700">
                            {`(+${country.phoneCode})`}
                          </span>
                        )}
                        <span
                          className={cn('font-normal truncate text-sm', {
                            'font-semibold':
                              country.code === selectedValue?.code,
                          })}>
                          {country.name}
                        </span>
                      </li>
                    );
                  })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
